import React, { useState, useEffect, useMemo } from "react";
import "./AdminView.css";
import { listenToCarpoolLocations, addCarpoolLocation, deleteCarpoolLocation } from "../services/carpoolLocationsService";
import { listenToSpecialEvents, listenToExcludedDates, addSpecialEvent, deleteSpecialEvent, addExcludedDate, deleteExcludedDate, updateSpecialEvent } from "../services/specialEventsService";
import { listenToHabitualCarpools, addHabitualCarpool, deleteHabitualCarpool } from "../services/habitualCarpoolsService";
import { buildMeetingDate, toIsoDate, formatMeetingLabel, addParisDays } from "../utils/dateHelpers";

const AdminView = ({ onBack }) => {
  const [locations, setLocations] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const [excludedDates, setExcludedDates] = useState([]);
  const [habitualCarpools, setHabitualCarpools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("parkings"); // "parkings", "events", "excluded", "habitual"
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [showAddHabitualForm, setShowAddHabitualForm] = useState(false);
  const [showHabitualDriverIconPicker, setShowHabitualDriverIconPicker] = useState(false);
  const [showHabitualPassengerIconPicker, setShowHabitualPassengerIconPicker] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    postalCode: "",
    latitude: "",
    longitude: "",
  });
  const [eventFormData, setEventFormData] = useState({
    name: "",
    date: "",
    time: "09:30",
    color: "orange",
  });
  const [selectedMeetingsToExclude, setSelectedMeetingsToExclude] = useState(new Set());
  const [selectedMonth, setSelectedMonth] = useState(""); // Format YYYY-MM
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [habitualFormData, setHabitualFormData] = useState({
    driverName: "",
    driverIcon: undefined,
    passengerName: "",
    passengerIcon: undefined,
  });

  useEffect(() => {
    console.log("🔧 [AdminView] Démarrage de l'écoute");
    
    let locationsLoaded = false;
    let eventsLoaded = false;
    let datesLoaded = false;
    let habitualLoaded = false;
    
    const checkAllLoaded = () => {
      if (locationsLoaded && eventsLoaded && datesLoaded && habitualLoaded) {
        setLoading(false);
      }
    };
    
    const unsubscribeLocations = listenToCarpoolLocations((locationsList) => {
      console.log("🔧 [AdminView] Adresses reçues:", locationsList.length);
      setLocations(locationsList);
      locationsLoaded = true;
      checkAllLoaded();
    });

    const unsubscribeEvents = listenToSpecialEvents((eventsList) => {
      console.log("🔧 [AdminView] Événements reçus:", eventsList.length);
      setSpecialEvents(eventsList);
      eventsLoaded = true;
      checkAllLoaded();
    });

    const unsubscribeDates = listenToExcludedDates((datesList) => {
      console.log("🔧 [AdminView] Dates exclues reçues:", datesList.length);
      setExcludedDates(datesList);
      datesLoaded = true;
      checkAllLoaded();
    });

    const unsubscribeHabitual = listenToHabitualCarpools((items) => {
      setHabitualCarpools(items);
      habitualLoaded = true;
      checkAllLoaded();
    });

    return () => {
      console.log("🔧 [AdminView] Nettoyage de l'écoute");
      if (unsubscribeLocations) unsubscribeLocations();
      if (unsubscribeEvents) unsubscribeEvents();
      if (unsubscribeDates) unsubscribeDates();
      if (unsubscribeHabitual) unsubscribeHabitual();
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      // Validation
      if (!formData.name.trim()) {
        throw new Error("Le nom est requis");
      }
      if (!formData.city.trim()) {
        throw new Error("La ville est requise");
      }
      if (!formData.postalCode.trim()) {
        throw new Error("Le code postal est requis");
      }
      if (!formData.address.trim() && !formData.latitude && !formData.longitude) {
        throw new Error("Une adresse ou des coordonnées GPS sont requises");
      }

      // Validation des coordonnées GPS si fournies
      if (formData.latitude || formData.longitude) {
        const lat = parseFloat(formData.latitude);
        const lng = parseFloat(formData.longitude);
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          throw new Error("Les coordonnées GPS sont invalides");
        }
      }

      const result = await addCarpoolLocation(formData);
      
      if (result.success) {
        // Réinitialiser le formulaire
        setFormData({
          name: "",
          address: "",
          city: "",
          postalCode: "",
          latitude: "",
          longitude: "",
        });
        setShowAddForm(false);
        alert("✅ Adresse de covoiturage ajoutée avec succès !");
      } else {
        throw new Error(result.error || "Erreur lors de l'ajout");
      }
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (locationId, locationName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${locationName}" ?`)) {
      return;
    }

    const result = await deleteCarpoolLocation(locationId);
    if (result.success) {
      alert("✅ Adresse supprimée avec succès !");
    } else {
      alert(`❌ Erreur: ${result.error || "Impossible de supprimer l'adresse"}`);
    }
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEventFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleHabitualInputChange = (e) => {
    const { name, value } = e.target;
    setHabitualFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      // Convertir date (YYYY-MM-DD) et time (HH:MM) en year, month, day, hour, minute
      if (!eventFormData.date || !eventFormData.time) {
        throw new Error("Tous les champs sont requis");
      }

      const [year, month, day] = eventFormData.date.split('-').map(Number);
      const [hour, minute] = eventFormData.time.split(':').map(Number);

      const eventData = {
        name: eventFormData.name,
        year,
        month,
        day,
        hour,
        minute,
        color: eventFormData.color || 'orange',
      };

      const result = await addSpecialEvent(eventData);
      
      if (result.success) {
        setEventFormData({
          name: "",
          date: "",
          time: "09:30",
          color: "orange",
        });
        setShowAddEventForm(false);
        alert("✅ Événement ajouté avec succès !");
      } else {
        throw new Error(result.error || "Erreur lors de l'ajout");
      }
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${eventName}" ?`)) {
      return;
    }

    const result = await deleteSpecialEvent(eventId);
    if (result.success) {
      alert("✅ Événement supprimé avec succès !");
    } else {
      alert(`❌ Erreur: ${result.error || "Impossible de supprimer l'événement"}`);
    }
  };

  const handleDeleteExcluded = async (dateId, isoDate) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réintégrer la date "${isoDate}" ?`)) {
      return;
    }

    const result = await deleteExcludedDate(dateId);
    if (result.success) {
      alert("✅ Date réintégrée avec succès !");
    } else {
      alert(`❌ Erreur: ${result.error || "Impossible de supprimer la date"}`);
    }
  };

  const handleSubmitHabitual = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const result = await addHabitualCarpool(habitualFormData);
      if (result.success) {
        setHabitualFormData({
          driverName: "",
          driverIcon: undefined,
          passengerName: "",
          passengerIcon: undefined,
        });
        setShowAddHabitualForm(false);
        setShowHabitualDriverIconPicker(false);
        setShowHabitualPassengerIconPicker(false);
        alert("✅ Covoiturage habituel ajouté !");
      } else {
        throw new Error(result.error || "Erreur lors de l'ajout");
      }
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteHabitual = async (itemId, label) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${label}" ?`)) return;
    const result = await deleteHabitualCarpool(itemId);
    if (result.success) {
      alert("✅ Covoiturage habituel supprimé !");
    } else {
      alert(`❌ Erreur: ${result.error || "Impossible de supprimer"}`);
    }
  };

  // Fonction pour obtenir les réunions d'un mois donné
  const meetingsForMonth = useMemo(() => {
    if (!selectedMonth) return [];
    
    const [year, month] = selectedMonth.split('-').map(Number);
    const results = [];
    
    // Date de début du mois (premier jour) - utiliser une date de référence pour le fuseau horaire de Paris
    const monthStart = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
    
    // Trouver le premier mercredi et samedi du mois ou avant
    let nextWed = buildMeetingDate(3, 19, 30, monthStart);
    let nextSat = buildMeetingDate(6, 18, 0, monthStart);
    
    // Si la première réunion est avant le début du mois, avancer d'une semaine
    if (toIsoDate(nextWed.getTime()).startsWith(selectedMonth) === false) {
      nextWed = addParisDays(nextWed, 7);
    }
    if (toIsoDate(nextSat.getTime()).startsWith(selectedMonth) === false) {
      nextSat = addParisDays(nextSat, 7);
    }
    
    const excludedSet = new Set(excludedDates.map(d => d.isoDate));
    const monthPrefix = selectedMonth; // Format YYYY-MM
    
    // Générer toutes les réunions du mois (maximum 10 semaines pour couvrir un mois)
    for (let i = 0; i < 10; i++) {
      const wedIsoDate = toIsoDate(nextWed.getTime());
      const satIsoDate = toIsoDate(nextSat.getTime());
      
      if (wedIsoDate.startsWith(monthPrefix)) {
        if (!excludedSet.has(wedIsoDate)) {
          results.push({
            meetingAt: nextWed.getTime(),
            isoDate: wedIsoDate,
            label: formatMeetingLabel(nextWed),
            weekday: 3,
          });
        }
        nextWed = addParisDays(nextWed, 7);
      }
      
      if (satIsoDate.startsWith(monthPrefix)) {
        if (!excludedSet.has(satIsoDate)) {
          results.push({
            meetingAt: nextSat.getTime(),
            isoDate: satIsoDate,
            label: formatMeetingLabel(nextSat),
            weekday: 6,
          });
        }
        nextSat = addParisDays(nextSat, 7);
      }
      
      // Si les deux dates sont hors du mois, arrêter
      if (!wedIsoDate.startsWith(monthPrefix) && !satIsoDate.startsWith(monthPrefix)) {
        break;
      }
    }
    
    return results.sort((a, b) => a.meetingAt - b.meetingAt);
  }, [selectedMonth, excludedDates]);

  // Fonction pour basculer la sélection d'une réunion
  const toggleMeetingSelection = (isoDate) => {
    setSelectedMeetingsToExclude(prev => {
      const newSet = new Set(prev);
      if (newSet.has(isoDate)) {
        newSet.delete(isoDate);
      } else {
        newSet.add(isoDate);
      }
      return newSet;
    });
  };

  // Fonction pour exclure les réunions sélectionnées
  const handleExcludeSelectedMeetings = async () => {
    const count = selectedMeetingsToExclude.size;
    if (count === 0) {
      alert("⚠️ Veuillez sélectionner au moins une réunion à exclure.");
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir exclure ${count} réunion(s) ?`)) {
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const promises = Array.from(selectedMeetingsToExclude).map(isoDate => 
        addExcludedDate({ isoDate, reason: "" })
      );

      const results = await Promise.all(promises);
      const failed = results.filter(r => !r.success);
      
      if (failed.length > 0) {
        throw new Error(`${failed.length} réunion(s) n'ont pas pu être exclue(s)`);
      }

      setSelectedMeetingsToExclude(new Set());
      alert(`✅ ${count} réunion(s) exclue(s) avec succès !`);
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-view">
        <div className="admin-header">
          <button className="admin-back-button" onClick={onBack}>
            ← Retour
          </button>
          <h1>🔧 Vue Administrateur</h1>
        </div>
        <div className="admin-loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="admin-header">
        <button className="admin-back-button" onClick={onBack}>
          ← Retour
        </button>
        <h1>🔧 Vue Administrateur</h1>
        <p className="admin-subtitle">Gestion des parkings, événements et réunions</p>
      </div>

      {/* Onglets */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "parkings" ? "active" : ""}`}
          onClick={() => setActiveTab("parkings")}
        >
          🅿️ Parkings
        </button>
        <button
          className={`admin-tab ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          📅 Événements
        </button>
        <button
          className={`admin-tab ${activeTab === "excluded" ? "active" : ""}`}
          onClick={() => setActiveTab("excluded")}
        >
          🚫 Réunions exclues
        </button>
        <button
          className={`admin-tab ${activeTab === "habitual" ? "active" : ""}`}
          onClick={() => setActiveTab("habitual")}
        >
          🔁 Habituels
        </button>
      </div>

      <div className="admin-content">
        {/* Onglet Parkings */}
        {activeTab === "parkings" && (
          <>
            <div className="admin-actions">
              <button
                className="admin-add-button"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? "✕ Annuler" : "+ Ajouter un parking"}
              </button>
            </div>

        {showAddForm && (
          <div className="admin-form-container">
            <form onSubmit={handleSubmit} className="admin-form">
              <h3>Nouvelle adresse de covoiturage</h3>
              
              {error && <div className="admin-error">{error}</div>}

              <div className="admin-form-group">
                <label htmlFor="name">Nom * (ex: Parking Monsabré)</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Parking Monsabré"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="address">Adresse</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Rue du père Monsabré"
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label htmlFor="city">Ville *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Blois"
                    required
                  />
                </div>

                <div className="admin-form-group">
                  <label htmlFor="postalCode">Code postal *</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="41000"
                    required
                  />
                </div>
              </div>

              <div className="admin-form-section">
                <p className="admin-form-hint">
                  <strong>OU</strong> coordonnées GPS (si l'adresse n'est pas trouvée)
                </p>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label htmlFor="latitude">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      id="latitude"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      placeholder="47.5936"
                    />
                  </div>

                  <div className="admin-form-group">
                    <label htmlFor="longitude">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      id="longitude"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      placeholder="1.3342"
                    />
                  </div>
                </div>
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-form-cancel"
                  onClick={() => {
                    setShowAddForm(false);
                    setError("");
                    setFormData({
                      name: "",
                      address: "",
                      city: "",
                      postalCode: "",
                      latitude: "",
                      longitude: "",
                    });
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="admin-form-submit"
                  disabled={submitting}
                >
                  {submitting ? "Ajout..." : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="admin-locations-list">
          <h3>Adresses de covoiturage ({locations.length})</h3>
          
          {locations.length === 0 ? (
            <div className="admin-empty">
              <p>Aucune adresse de covoiturage enregistrée.</p>
              <p>Cliquez sur "Ajouter une adresse" pour en créer une.</p>
            </div>
          ) : (
            <div className="admin-locations-grid">
              {locations.map((location) => (
                <div key={location.id} className="admin-location-card">
                  <div className="admin-location-header">
                    <h4>{location.name}</h4>
                    <button
                      className="admin-delete-button"
                      onClick={() => handleDelete(location.id, location.name)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="admin-location-details">
                    {location.address && (
                      <p><strong>Adresse:</strong> {location.address}</p>
                    )}
                    <p><strong>Ville:</strong> {location.city} {location.postalCode}</p>
                    {location.latitude && location.longitude && (
                      <p><strong>GPS:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {/* Onglet Événements */}
        {activeTab === "events" && (
          <>
            <div className="admin-actions">
              <button
                className="admin-add-button"
                onClick={() => setShowAddEventForm(!showAddEventForm)}
              >
                {showAddEventForm ? "✕ Annuler" : "+ Ajouter un événement"}
              </button>
            </div>

            {showAddEventForm && (
              <div className="admin-form-container">
                <form onSubmit={handleSubmitEvent} className="admin-form">
                  <h3>Nouvel événement spécial</h3>
                  
                  {error && <div className="admin-error">{error}</div>}

                  <div className="admin-form-group">
                    <label htmlFor="eventName">Nom de l'événement *</label>
                    <input
                      type="text"
                      id="eventName"
                      name="name"
                      value={eventFormData.name}
                      onChange={handleEventInputChange}
                      placeholder="Assemblée de circonscription"
                      required
                    />
                  </div>

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label htmlFor="eventDate">Date *</label>
                      <input
                        type="date"
                        id="eventDate"
                        name="date"
                        value={eventFormData.date}
                        onChange={handleEventInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="admin-form-group">
                      <label htmlFor="eventTime">Heure *</label>
                      <input
                        type="time"
                        id="eventTime"
                        name="time"
                        value={eventFormData.time}
                        onChange={handleEventInputChange}
                        required
                      />
                    </div>
                    <div className="admin-form-group">
                      <label htmlFor="eventColor">Couleur *</label>
                      <select
                        id="eventColor"
                        name="color"
                        value={eventFormData.color}
                        onChange={handleEventInputChange}
                        required
                      >
                        <option value="orange">Orange</option>
                        <option value="blue">Bleu</option>
                        <option value="green">Vert</option>
                        <option value="red">Rouge</option>
                        <option value="purple">Violet</option>
                      </select>
                    </div>
                  </div>

                  <div className="admin-form-actions">
                    <button
                      type="button"
                      className="admin-form-cancel"
                      onClick={() => {
                        setShowAddEventForm(false);
                        setError("");
                        setEventFormData({
                          name: "",
                          date: "",
                          time: "09:30",
                          color: "orange",
                        });
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="admin-form-submit"
                      disabled={submitting}
                    >
                      {submitting ? "Ajout..." : "Ajouter"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-locations-list">
              <h3>Événements spéciaux ({specialEvents.length})</h3>
              
              {specialEvents.length === 0 ? (
                <div className="admin-empty">
                  <p>Aucun événement spécial enregistré.</p>
                </div>
              ) : (
                <div className="admin-locations-grid">
                  {specialEvents.map((event) => (
                    <div key={event.id} className="admin-location-card">
                      <div className="admin-location-header">
                        <h4>{event.name}</h4>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select
                            value={event.color || 'orange'}
                            onChange={async (e) => {
                              const newColor = e.target.value;
                              console.log('🔄 [AdminView] Changement couleur:', event.id, 'de', event.color, 'vers', newColor);
                              const result = await updateSpecialEvent(event.id, { color: newColor });
                              if (result.success) {
                                console.log('✅ [AdminView] Couleur mise à jour');
                              } else {
                                alert('❌ Erreur lors de la mise à jour de la couleur');
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              fontSize: '12px',
                            }}
                          >
                            <option value="orange">Orange</option>
                            <option value="green">Vert</option>
                            <option value="blue">Bleu</option>
                            <option value="red">Rouge</option>
                            <option value="purple">Violet</option>
                          </select>
                          <button
                            className="admin-delete-button"
                            onClick={() => handleDeleteEvent(event.id, event.name)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="admin-location-details">
                        <p><strong>Date:</strong> {event.day}/{event.month}/{event.year} à {String(event.hour).padStart(2, '0')}:{String(event.minute).padStart(2, '0')}</p>
                        <p><strong>Couleur actuelle:</strong> <span style={{ color: event.color === 'orange' ? '#f97316' : event.color === 'blue' ? '#3b82f6' : event.color === 'green' ? '#10b981' : event.color === 'red' ? '#ef4444' : '#a855f7', fontWeight: 'bold' }}>{event.color || 'orange'}</span></p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Onglet Dates exclues */}
        {activeTab === "excluded" && (
          <>
            <div className="admin-actions">
              <div className="admin-form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="monthSelector" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Sélectionner un mois :
                </label>
                <input
                  type="month"
                  id="monthSelector"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setSelectedMeetingsToExclude(new Set()); // Réinitialiser la sélection
                  }}
                  style={{
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '300px'
                  }}
                />
              </div>
            </div>

            {error && <div className="admin-error" style={{ marginBottom: '1rem' }}>{error}</div>}

            {selectedMonth && (
              <div className="admin-locations-list">
                <h3>
                  Réunions de {new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </h3>
                
                {meetingsForMonth.length === 0 ? (
                  <div className="admin-empty">
                    <p>Toutes les réunions de ce mois sont déjà exclues.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '0.5rem',
                      marginBottom: '1rem',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      {meetingsForMonth.map((meeting) => {
                        const isSelected = selectedMeetingsToExclude.has(meeting.isoDate);
                        const isExcluded = excludedDates.some(d => d.isoDate === meeting.isoDate);
                        
                        return (
                          <label
                            key={meeting.isoDate}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0.75rem',
                              background: isExcluded ? '#fee2e2' : (isSelected ? '#dbeafe' : '#f9fafb'),
                              border: isExcluded ? '2px solid #ef4444' : (isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb'),
                              borderRadius: '8px',
                              cursor: isExcluded ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s',
                              opacity: isExcluded ? 0.6 : 1
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => !isExcluded && toggleMeetingSelection(meeting.isoDate)}
                              disabled={isExcluded}
                              style={{ marginRight: '0.75rem', width: '20px', height: '20px', cursor: isExcluded ? 'not-allowed' : 'pointer' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: isExcluded ? '#991b1b' : '#1f2937' }}>
                                {meeting.label}
                                {isExcluded && <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#dc2626' }}>(Déjà exclue)</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    
                    {selectedMeetingsToExclude.size > 0 && (
                      <div className="admin-form-actions" style={{ marginTop: '1rem' }}>
                        <button
                          type="button"
                          className="admin-form-cancel"
                          onClick={() => setSelectedMeetingsToExclude(new Set())}
                        >
                          Annuler la sélection
                        </button>
                        <button
                          type="button"
                          className="admin-form-submit"
                          onClick={handleExcludeSelectedMeetings}
                          disabled={submitting}
                        >
                          {submitting ? "Exclusion..." : `Exclure ${selectedMeetingsToExclude.size} réunion(s)`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {!selectedMonth && (
              <div className="admin-empty">
                <p>Veuillez sélectionner un mois pour voir les réunions disponibles.</p>
              </div>
            )}

            <div className="admin-locations-list" style={{ marginTop: '2rem' }}>
              <h3>Réunions exclues ({excludedDates.length})</h3>
              
              {excludedDates.length === 0 ? (
                <div className="admin-empty">
                  <p>Aucune réunion exclue.</p>
                </div>
              ) : (
                <div className="admin-locations-grid">
                  {excludedDates.map((date) => (
                    <div key={date.id} className="admin-location-card">
                      <div className="admin-location-header">
                        <h4>{date.isoDate}</h4>
                        <button
                          className="admin-delete-button"
                          onClick={() => handleDeleteExcluded(date.id, date.isoDate)}
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="admin-location-details">
                        {date.reason && <p><strong>Raison:</strong> {date.reason}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Onglet Habituels */}
        {activeTab === "habitual" && (
          <>
            <div className="admin-actions">
              <button
                className="admin-add-button"
                onClick={() => setShowAddHabitualForm(!showAddHabitualForm)}
              >
                {showAddHabitualForm ? "✕ Annuler" : "+ Ajouter un habituel"}
              </button>
            </div>

            {showAddHabitualForm && (
              <div className="admin-form-container">
                <form onSubmit={handleSubmitHabitual} className="admin-form">
                  <h3>Nouveau covoiturage habituel</h3>

                  {error && <div className="admin-error">{error}</div>}

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label htmlFor="habitualDriverName">Conducteur *</label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          id="habitualDriverName"
                          name="driverName"
                          value={habitualFormData.driverName}
                          onChange={handleHabitualInputChange}
                          placeholder="Nom du conducteur"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowHabitualDriverIconPicker((v) => !v);
                            setShowHabitualPassengerIconPicker(false);
                          }}
                          aria-label="Choisir une icône véhicule"
                          style={{
                            position: "absolute",
                            right: 8,
                            top: 36,
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 2px rgba(0,0,0,.06)",
                            cursor: "pointer",
                            fontSize: 20,
                          }}
                        >
                          {habitualFormData.driverIcon || "🚗"}
                        </button>
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label htmlFor="habitualPassengerName">Passager *</label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          id="habitualPassengerName"
                          name="passengerName"
                          value={habitualFormData.passengerName}
                          onChange={handleHabitualInputChange}
                          placeholder="Nom du passager"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowHabitualPassengerIconPicker((v) => !v);
                            setShowHabitualDriverIconPicker(false);
                          }}
                          aria-label="Choisir une icône de profil"
                          style={{
                            position: "absolute",
                            right: 8,
                            top: 36,
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 2px rgba(0,0,0,.06)",
                            cursor: "pointer",
                            fontSize: 20,
                          }}
                        >
                          {habitualFormData.passengerIcon || "👤"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {showHabitualDriverIconPicker && (
                    <div className="admin-form-group">
                      <label>Icône véhicule (facultatif) :</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {["", "🚗", "🚙", "🚕", "🛻", "🚐", "🚚", "🏎️", "🚘"].map((emo) => (
                          <button
                            key={emo || "none-driver"}
                            type="button"
                            onClick={() =>
                              setHabitualFormData((prev) => ({
                                ...prev,
                                driverIcon: emo || undefined,
                              }))
                            }
                            aria-label={emo ? `Choisir ${emo}` : "Aucune icône"}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              border:
                                (habitualFormData.driverIcon || "") === emo
                                  ? "2px solid #7c3aed"
                                  : "1px solid #d1d5db",
                              background: "#fff",
                              cursor: "pointer",
                              fontSize: 20,
                            }}
                          >
                            {emo || "—"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {showHabitualPassengerIconPicker && (
                    <div className="admin-form-group">
                      <label>Icône du profil (facultatif) :</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {[
                          "",
                          "👶",
                          "🧒",
                          "👧",
                          "🧑",
                          "👨",
                          "👩",
                          "👨‍🦱",
                          "👩‍🦱",
                          "👨‍🦰",
                          "👩‍🦰",
                          "👴",
                          "👵",
                          "👤",
                          "👥",
                          "🧑‍🦳",
                          "👩‍🦳",
                        ].map((emo) => (
                          <button
                            key={emo || "none-passenger"}
                            type="button"
                            onClick={() =>
                              setHabitualFormData((prev) => ({
                                ...prev,
                                passengerIcon: emo || undefined,
                              }))
                            }
                            aria-label={emo ? `Choisir ${emo}` : "Aucune icône"}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              border:
                                (habitualFormData.passengerIcon || "") === emo
                                  ? "2px solid #7c3aed"
                                  : "1px solid #d1d5db",
                              background: "#fff",
                              cursor: "pointer",
                              fontSize: 20,
                            }}
                          >
                            {emo || "—"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="admin-form-actions">
                    <button
                      type="button"
                      className="admin-form-cancel"
                      onClick={() => {
                        setShowAddHabitualForm(false);
                        setError("");
                        setHabitualFormData({
                          driverName: "",
                          driverIcon: undefined,
                          passengerName: "",
                          passengerIcon: undefined,
                        });
                        setShowHabitualDriverIconPicker(false);
                        setShowHabitualPassengerIconPicker(false);
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="admin-form-submit"
                      disabled={submitting}
                    >
                      {submitting ? "Ajout..." : "Ajouter"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-locations-list">
              <h3>Covoiturages habituels ({habitualCarpools.length})</h3>

              {habitualCarpools.length === 0 ? (
                <div className="admin-empty">
                  <p>Aucun covoiturage habituel enregistré.</p>
                </div>
              ) : (
                <div className="admin-locations-grid">
                  {habitualCarpools
                    .slice()
                    .sort((a, b) => {
                      const ad = String(a.driverName || "").toLowerCase();
                      const bd = String(b.driverName || "").toLowerCase();
                      const ap = String(a.passengerName || "").toLowerCase();
                      const bp = String(b.passengerName || "").toLowerCase();
                      return ad.localeCompare(bd) || ap.localeCompare(bp);
                    })
                    .map((item) => {
                      const dIcon = item.driverIcon || "🚗";
                      const pIcon = item.passengerIcon || "👤";
                      const label = `${dIcon} ${item.driverName || "?"} → ${pIcon} ${item.passengerName || "?"}`;
                      return (
                        <div key={item.id} className="admin-location-card">
                          <div className="admin-location-header">
                            <h4>{label}</h4>
                            <button
                              className="admin-delete-button"
                              onClick={() => handleDeleteHabitual(item.id, label)}
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </div>
                          <div className="admin-location-details">
                            <p><strong>Conducteur:</strong> {item.driverName}</p>
                            <p><strong>Passager:</strong> {item.passengerName}</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminView;
